import React, { useState, useMemo } from 'react';
import { OrderComparison } from '../../../types/comparative';
import DataTable from '../../ui/DataTable';
import LoadingSpinner from '../../LoadingSpinner';
import { ORDER_STATES } from '../../../assets/data/arrays';
import { EyeIcon } from '@heroicons/react/24/outline';

interface ComparisonResultsProps {
  comparisons: OrderComparison[];
  isLoading?: boolean;
  onViewDetails?: (comparison: OrderComparison) => void;
  onSyncOrder?: (orderId: string) => Promise<void>;
  selectedStore?: string;
}

const ComparisonResults: React.FC<ComparisonResultsProps> = ({
  comparisons,
  isLoading = false,
  onViewDetails,
  onSyncOrder,
  selectedStore
}) => {
  const [filter, setFilter] = useState<'all' | 'complete' | 'missing' | 'discrepancy'>('all');
  const [syncingOrders, setSyncingOrders] = useState<Set<string>>(new Set());

  const filteredComparisons = useMemo(() => {
    if (filter === 'all') return comparisons;
    return comparisons.filter(comp => comp.comparison.syncStatus === filter);
  }, [comparisons, filter]);

  const handleSyncOrder = async (orderId: string) => {
    if (!onSyncOrder) return;
    
    setSyncingOrders(prev => new Set(prev).add(orderId));
    try {
      await onSyncOrder(orderId);
    } catch (error) {
      console.error('Error sincronizando pedido:', error);
    } finally {
      setSyncingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const isOrderCancelled = (order: OrderComparison): boolean => {
    if (!order.sglOrder) return false;
    return order.sglOrder.status === 'eliminado' || 
           order.sglOrder.status === 'verify_transaction' || 
           order.sglOrder.status === 'cancelled' ||
           order.sglOrder.status === 'pending';
  };

  const getSyncStatusBadge = (status: 'complete' | 'missing' | 'discrepancy') => {
    const baseClasses = "inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'complete':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'missing':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'discrepancy':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getSyncStatusIcon = (status: 'complete' | 'missing' | 'discrepancy') => {
    switch (status) {
      case 'complete':
        return (
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'missing':
        return (
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      case 'discrepancy':
        return (
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  // Función para mostrar información de debug
  const getDebugInfo = (comparison: OrderComparison) => {
    const sglOrder = comparison.sglOrder;
    const ecommerceOrder = comparison.ecommerceOrder;
    
    if (!sglOrder) {
      return {
        sglId: 'Sin datos SGL',
        sglOrdersId: '-',
        sglOriginOrderId: '-',
        ecommerceId: ecommerceOrder?.id || '-',
        ecommerceOrderNumber: ecommerceOrder?.orderNumber || '-',
        matchType: comparison.comparison.matchType || 'none'
      };
    }
    
    return {
      sglId: sglOrder.id,
      sglOrdersId: sglOrder.orders_id,
      sglOriginOrderId: sglOrder.origin_order_id,
      ecommerceId: ecommerceOrder?.id,
      ecommerceOrderNumber: ecommerceOrder?.orderNumber,
      matchType: comparison.comparison.matchType
    };
  };

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString('es-CO')}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      // Verificar si la fecha es válida
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Mostrar el valor original si no se puede formatear
      }
      
      return date.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return dateString; // Mostrar el valor original si hay error
    }
  };

  // Función para obtener textos dinámicos según la tienda seleccionada
  const getStoreTexts = () => {
    if (selectedStore === 'OFIMA') {
      return {
        externalId: 'ID OFIMA',
        externalTotal: 'Total OFIMA',
        rcExternal: '#RC OFIMA',
        statusHeader: 'Estado en OFIMA',
        existsText: 'Si Existe',
        notExistsText: 'No existe',
        existsFilter: 'Existen en OFIMA',
        notExistsFilter: 'No existen en OFIMA',
        sglDate: 'Fecha SGL',
        externalDate: 'Fecha OFIMA'
      };
    } else {
      return {
        externalId: 'ID Ecommerce',
        externalTotal: 'Total Ecommerce',
        rcExternal: 'RC de ERP',
        statusHeader: 'Estado en SGL',
        existsText: 'Existe en SGL',
        notExistsText: 'No existe en SGL',
        existsFilter: 'Existen en SGL',
        notExistsFilter: 'No existen en SGL',
        sglDate: 'Fecha SGL',
        externalDate: 'Fecha Ecommerce'
      };
    }
  };

  const storeTexts = getStoreTexts();

   const columns = [
     {
       header: 'ID SGL',
       accessor: 'sglOrder' as keyof OrderComparison,
       cell: (row: OrderComparison) => (
         <span className="font-mono text-xs">
           {row.sglOrder ? row.sglOrder.id : '-'}
         </span>
       ),
       width: '80px',
     },
     {
       header: storeTexts.externalId,
       accessor: 'ecommerceOrder' as keyof OrderComparison,
       cell: (row: OrderComparison) => (
         <span className="font-mono text-xs">{row.ecommerceOrder?.id || '-'}</span>
       ),
       width: '80px',
     },
    /*  {
       header: '# RC SGL',
       accessor: 'sglOrder' as keyof OrderComparison,
       cell: (row: OrderComparison) => (
         <span className="font-mono text-sm text-blue-600">
           {row.sglOrder ? (row.sglOrder.id_remision || '-') : '-'}
         </span>
       ),
       width: '120px',
     }, */
     // Solo mostrar RC de ERP para OFIMA
     ...(selectedStore === 'OFIMA' ? [{
       header: storeTexts.rcExternal,
       accessor: 'ecommerceOrder' as keyof OrderComparison,
       cell: (row: OrderComparison) => (
         <span className="font-mono text-xs text-green-600">
           {row.ecommerceOrder ? (row.ecommerceOrder.id_remision || '-') : '-'}
         </span>
       ),
       width: '80px',
     }] : []),

   /*  {
      header: 'Cliente',
      accessor: 'ecommerceOrder' as keyof OrderComparison,
      cell: (row: OrderComparison) => (
        <span className="text-sm">
          {row.ecommerceOrder?.customerName || 'N/A'}
        </span>
      ),
    }, */
  /*   {
      header: 'Estado Ecommerce',
      accessor: 'ecommerceOrder' as keyof OrderComparison,
      cell: (row: OrderComparison) => (
        <span className="text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
          {row.ecommerceOrder?.status || '-'}
        </span>
      ),
      width: '140px',
    },
     */
     {
       header: 'Estado SGL',
       accessor: 'sglOrder' as keyof OrderComparison,
       cell: (row: OrderComparison) => {
         const state = ORDER_STATES.find(s => s.value === row.sglOrder?.status);
         
         if (!state) {
           return (
             <span className="text-center text-xs font-medium max-w-[70px] w-full truncate inline-block px-1 py-1 rounded text-gray-500 border border-sky-500">
               {row.sglOrder?.status || '-'}
             </span>
           );
         }
         
         return (
           <span className={`text-center text-xs font-medium max-w-[70px] w-full truncate inline-block px-1 py-1 rounded ${state.color}`}>
             {state.label}
           </span>
         );
       },
       width: '100px',
     },
     {
       header: 'Estado Ecommerce',
       accessor: 'ecommerceOrder' as keyof OrderComparison,
       cell: (row: OrderComparison) => {
         if (!row.ecommerceOrder?.status) {
           return (
             <span className="text-center text-xs font-medium max-w-[70px] w-full truncate inline-block px-1 py-1 rounded text-gray-500 border border-sky-500">
               -
             </span>
           );
         }
         
         const state = ORDER_STATES.find(s => s.value === row.ecommerceOrder?.status);
         
         if (!state) {
           return (
             <span className="text-center text-xs font-medium max-w-[70px] w-full truncate inline-block px-1 py-1 rounded text-gray-500 border border-sky-500">
               {row.ecommerceOrder.status}
             </span>
           );
         }
         
         return (
           <span className={`text-center text-xs font-medium max-w-[70px] w-full truncate inline-block px-1 py-1 rounded ${state.color}`}>
             {state.label}
           </span>
         );
       },
       width: '100px',
     },
    {
      header: 'Total SGL',
      accessor: 'sglOrder' as keyof OrderComparison,
      cell: (row: OrderComparison) => (
        <span className="font-semibold text-xs">
          {row.sglOrder ? formatCurrency(parseFloat(row.sglOrder.total)) : '-'}
        </span>
      ),
      width: '100px',
     }, {
       header: storeTexts.externalTotal,
       accessor: 'ecommerceOrder' as keyof OrderComparison,
       cell: (row: OrderComparison) => (
         <span className="font-semibold text-xs">
           {row.ecommerceOrder ? formatCurrency(row.ecommerceOrder.total) : '-'}
         </span>
       ),
       width: '100px',
     },
    // Mostrar fechas para todas las tiendas
    {
      header: storeTexts.sglDate,
      accessor: 'sglOrder' as keyof OrderComparison,
      cell: (row: OrderComparison) => {
        return (
          <span className="text-xs text-gray-600">
            {row.sglOrder ? formatDate(row.sglOrder.date_created) : '-'}
          </span>
        );
      },
      width: '90px',
    },
    {
      header: storeTexts.externalDate,
      accessor: 'ecommerceOrder' as keyof OrderComparison,
      cell: (row: OrderComparison) => (
        <span className="text-xs text-gray-600">
          {row.ecommerceOrder ? formatDate(row.ecommerceOrder.date_modified || row.ecommerceOrder.dateCreated) : '-'}
        </span>
      ),
      width: '90px',
    },
/*     {
      header: 'Tipo de Match',
      accessor: 'comparison' as keyof OrderComparison,
      cell: (row: OrderComparison) => {
        const getMatchTypeLabel = (matchType?: string) => {
          switch (matchType) {
            case 'orders_id': return 'Orders ID';
            case 'origin_order_id': return 'Origin Order ID';
            case 'id': return 'ID Directo';
            case 'total_date': return 'Total + Fecha';
            case 'none': return 'Sin Match';
            default: return 'Desconocido';
          }
        };

        const getMatchTypeColor = (matchType?: string) => {
          switch (matchType) {
            case 'orders_id': return 'bg-green-100 text-green-800';
            case 'origin_order_id': return 'bg-blue-100 text-blue-800';
            case 'id': return 'bg-purple-100 text-purple-800';
            case 'total_date': return 'bg-yellow-100 text-yellow-800';
            case 'none': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
          }
        };

        return (
          <span className={`text-xs px-2 py-1 rounded-full ${getMatchTypeColor(row.comparison.matchType)}`}>
            {getMatchTypeLabel(row.comparison.matchType)}
          </span>
        );
      },
      width: '140px',
    }, */
       {
         header: storeTexts.statusHeader,
         accessor: 'comparison' as keyof OrderComparison,
         cell: (row: OrderComparison) => {
           // Para OFIMA, verificar primero si hay mensaje en ofima_data
           if (selectedStore === 'OFIMA') {
             console.log('OFIMA selected, checking data:', {
               selectedStore,
               ecommerceOrder: row.ecommerceOrder,
               ofima_data: row.ecommerceOrder?.ofima_data,
               mensaje: row.ecommerceOrder?.ofima_data?.mensaje
             });
             
             // Verificar si existe el campo mensaje y si tiene texto
             if (row.ecommerceOrder?.ofima_data?.mensaje && row.ecommerceOrder.ofima_data.mensaje.trim() !== '') {
               return (
                 <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                   {row.ecommerceOrder.ofima_data.mensaje}
                 </span>
               );
             }
           }
           
           // Lógica normal para otros casos
           return (
             <span className={getSyncStatusBadge(row.comparison.syncStatus)}>
               {getSyncStatusIcon(row.comparison.syncStatus)}
               {row.comparison.syncStatus === 'complete' && storeTexts.existsText}
               {row.comparison.syncStatus === 'missing' && storeTexts.notExistsText}
             </span>
           );
         },
         width: '120px',
       },
    // Columna de debug oculta en producción
    ...(false ? [{
      header: 'Debug Info',
      accessor: 'comparison' as keyof OrderComparison,
      cell: (row: OrderComparison) => {
        const debugInfo = getDebugInfo(row);
        return (
          <div className="text-xs text-gray-600 max-w-[200px]">
            <div className="truncate">
              <strong>SGL ID:</strong> {debugInfo.sglId}
            </div>
            <div className="truncate">
              <strong>Orders ID:</strong> {debugInfo.sglOrdersId || '-'}
            </div>
            <div className="truncate">
              <strong>E-commerce ID:</strong> {debugInfo.ecommerceId || '-'}
            </div>
            <div className="truncate">
              <strong>Order Number:</strong> {debugInfo.ecommerceOrderNumber || '-'}
            </div>
            <div className="truncate">
              <strong>Match:</strong> {debugInfo.matchType}
            </div>
          </div>
        );
      },
      width: '200px',
    }] : []),
    {
      header: 'Acciones',
      accessor: 'id' as keyof OrderComparison,
      cell: (row: OrderComparison) => {
        const orderId = row.sglOrder?.id?.toString() || '';
        const isSyncing = syncingOrders.has(orderId);
        const isCancelled = isOrderCancelled(row);
        const canSync = selectedStore === 'OFIMA' && !isCancelled && row.comparison.syncStatus === 'missing';
        
        return (
          <div className="flex space-x-1">
            <button
              onClick={() => onViewDetails?.(row)}
              className="text-blue-500 hover:text-blue-700 text-xs"
              title="Ver detalles"
            >
        <EyeIcon className="w-4 h-4" /> 
            </button>
            
            {canSync && (
              <button
                onClick={() => handleSyncOrder(orderId)}
                disabled={isSyncing}
                className={`text-xs px-1 py-1 rounded ${
                  isSyncing 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-orange-500 hover:text-orange-700 hover:bg-orange-50'
                }`}
                title={isCancelled ? 'Pedido cancelado' : 'Sincronizar con OFIMA'}
              >
                {isSyncing ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
              </button>
            )}
          </div>
        );
      },
      width: '80px',
    },
  ];

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="large" />
          <span className="ml-3 text-gray-600">Cargando comparación...</span>
        </div>
      </div>
    );
  }



  return (

 
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        {filteredComparisons.length > 0 && (
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-blue-800">
          Resultados de Comparación ({filteredComparisons.length} pedidos)
        </h3>
        
        {/* Filtros */}
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              filter === 'all' 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todos ({comparisons.length})
          </button>
          <button
            onClick={() => setFilter('complete')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              filter === 'complete' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {storeTexts.existsFilter} ({comparisons.filter(c => c.comparison.syncStatus === 'complete').length})
          </button>
          <button
            onClick={() => setFilter('missing')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              filter === 'missing' 
                ? 'bg-red-100 text-red-800' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {storeTexts.notExistsFilter} ({comparisons.filter(c => c.comparison.syncStatus === 'missing').length})
          </button>
          <button
            onClick={() => setFilter('discrepancy')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              filter === 'discrepancy' 
                ? 'bg-yellow-100 text-yellow-800' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Discrepancias ({comparisons.filter(c => c.comparison.syncStatus === 'discrepancy').length})
          </button>
        </div>
      </div>
)}
      <div className="overflow-x-auto">
        {filteredComparisons.length > 0 && (
        <DataTable
          data={filteredComparisons}
          columns={columns}
          className="w-full"
        />
        )}
      </div>
    </div>
  );
};

export default ComparisonResults;
